<script setup>
import { computed, useDevice, useImageSource } from '#imports';

const props = defineProps({
  imageSrc: {
    type: Object,
    default: undefined,
    required: true,
  },
  type: {
    type: String,
    default: 'withBlock',
  },
  titleImage: {
    type: Object,
    required: true,
  },
  titleImageMobile: {
    type: Object,
    required: false,
    default: null,
  },
  withCounter: {
    type: Boolean,
  },
  isPrideSpecial: {
    type: Boolean,
  },
});

function getImageSource({ dpr }) {
  return useImageSource({
    image: props.imageSrc,
    width: '1440',
    dpr,
  });
}

const { isMobile, isDesktop } = useDevice();

function getImageAssetId(image) {
  return image.asset._ref;
}

function getImageCaption(image) {
  return `Изображение-${getImageAssetId(image)}`;
}

const classes = computed(() => ({
  'cover__container--gradient': props.type === 'withGradient',
  'cover__container--color-block': props.type === 'withBlock',
}));
</script>

<template>
  <div
    :style="{
      backgroundImage: `url(${getImageSource({ dpr: 2 })})`,
    }"
    class="cover"
  >
    <div :class="classes" class="cover__container">
      <NuxtLink v-if="isPrideSpecial" :to="{ name: 'pride-feed' }">
        <SanityImage
          v-if="isDesktop && titleImage"
          :alt="getImageCaption(titleImage)"
          :asset-id="getImageAssetId(titleImage)"
          auto="format"
          class="cover__title"
        />
        <SanityImage
          v-if="isMobile && titleImageMobile"
          :alt="getImageCaption(titleImageMobile)"
          :asset-id="getImageAssetId(titleImageMobile)"
          auto="format"
          class="cover__title"
        />
      </NuxtLink>

      <SanityImage
        v-if="!isPrideSpecial && titleImage"
        :alt="getImageCaption(titleImage)"
        :asset-id="getImageAssetId(titleImage)"
        auto="format"
        class="cover__title"
      />
      <TitleCounter v-if="withCounter" />
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.cover {
  position: relative;
  height: 100%;
  background-repeat: no-repeat;
  background-position: top;
  background-size: cover;
  border-radius: 0 0 24px 24px;

  &__title {
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 920px;
    margin: 0 auto;
    color: #fff;
  }

  @media (--medium-vp) {
    &__title {
      display: flex;
      align-items: center;
      flex-direction: column;
      justify-content: center;
      width: calc(100% - 50px);
    }
  }

  &__container {
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: flex-end;
    width: 100%;

    &--color-block {
      overflow: hidden;
      padding-bottom: 20px;
      border-radius: 0 0 24px 24px;
      height: calc(100vh - var(--main-header-height));

      &::after {
        position: absolute;
        z-index: 1;
        right: 0;
        bottom: 0;
        left: 0;
        height: 260px;
        content: '';
        border-radius: 0 0 24px 24px;
        background: linear-gradient(
          180deg,
          transparent 0%,
          rgba(var(--violet-color-rgb), 1) 60.42%,
          var(--newsletter-cover-color) 100%
        );
      }

      &::before {
        position: absolute;
        z-index: 1;
        top: 0;
        right: 0;
        left: 0;
        height: 100px;
        content: '';
        background: linear-gradient(
          to top,
          transparent 0%,
          rgba(var(--violet-color-rgb), 0) 10%,
          var(--newsletter-cover-color) 100%
        );
      }

      @media (--small-vp) {
        height: calc(100vh - 100px);

        &::after {
          height: 200px;
        }

        &::before {
          height: 80px;
        }

        &__title {
          width: calc(100% - 16px);
        }
      }
    }

    &--gradient {
      height: 510px;

      &::before {
        position: absolute;
        top: 225px;
        display: block;
        width: 100%;
        height: 1000px;
        content: '';
        background: linear-gradient(
          180deg,
          rgba(149, 57, 168, 0%) 4.94%,
          #9f2bb6 23.22%,
          rgba(149, 57, 168, 0%) 42.1%
        );
      }

      & .cover__title {
        max-height: 172px;
      }

      @media (--small-vp) {
        height: 270px;

        &::before {
          top: 125px;
          display: block;
          width: 100%;
          height: 500px;
        }

        & .cover__title {
          width: calc(100% - 16px);
        }
      }
    }
  }
}
</style>
